// src/components/Admin/EstadisticaINE.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
    AppBar, Box, CircularProgress, Dialog, IconButton, Toolbar, Typography,
    Grid, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Button
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PrintIcon from "@mui/icons-material/Print";
import axios from "axios";

// ---------- Utils ----------
const sexoFmt = (vRaw) => {
    const s = (vRaw ?? "").toString().trim();
    if (s === "1") return "M"; // 1 = mujer
    if (s === "0") return "H"; // 0 = hombre
    return "";
};
const getJubKey = (estadoObjOrStr) => {
    if (!estadoObjOrStr) return "NC";
    const nombre = (typeof estadoObjOrStr === "object" ? estadoObjOrStr?.nombre : estadoObjOrStr) ?? "";
    if (nombre.toLowerCase().includes("jubil")) return "JUB";
    return "NO_JUB";
};
const leerFecha = (row) => row?.fecha_nacimiento ?? "";
const calcEdad = (iso) => {
    if (!iso) return null;
    const [y, m, d] = String(iso).split("-");
    if (!y || !m || !d) return null;
    const hoy = new Date();
    let edad = hoy.getFullYear() - Number(y);
    const mm = hoy.getMonth() + 1;
    const dd = hoy.getDate();
    if (mm < Number(m) || (mm === Number(m) && dd < Number(d))) edad -= 1;
    return Number.isFinite(edad) ? edad : null;
};
const bucketEdad = (edad) => {
    if (edad == null) return "NO_CONSTA";
    if (edad < 35) return "MENOS_35";
    if (edad <= 44) return "35_44";
    if (edad <= 54) return "45_54";
    if (edad <= 64) return "55_64";
    if (edad <= 69) return "65_69";
    return "70_MAS";
};
const getEspecialidadesIds = (row) => {
    const v1 = row?.especialidadesIds;
    const v2 = row?.especialidades_ids;
    const v3 = row?.especialidades;
    if (Array.isArray(v1)) return v1.map((x) => (typeof x === "object" ? x.id : x)).filter(Boolean);
    if (Array.isArray(v2)) return v2;
    if (Array.isArray(v3)) return v3.map((x) => (typeof x === "object" ? x.id : x)).filter(Boolean);
    if (typeof v3 === "string") return v3.split(",").map((s) => s.trim()).filter(Boolean);
    return [];
};
const initAges = () => ({
    MENOS_35: { total: 0, H: 0, M: 0 },
    "35_44": { total: 0, H: 0, M: 0 },
    "45_54": { total: 0, H: 0, M: 0 },
    "55_64": { total: 0, H: 0, M: 0 },
    "65_69": { total: 0, H: 0, M: 0 },
    "70_MAS": { total: 0, H: 0, M: 0 },
    NO_CONSTA: { total: 0, H: 0, M: 0 },
});
const initJub = () => ({
    JUB: { total: 0, H: 0, M: 0 },
    NO_JUB: { total: 0, H: 0, M: 0 },
    NC: { total: 0, H: 0, M: 0 }
});
const fmtFechaHora = (d = new Date()) => {
    const pad = (n) => String(n).padStart(2, "0");
    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

// ---------- Componente ----------
export default function EstadisticaINE({ open, onClose, usuario }) {
    const API = process.env.REACT_APP_API_URL;
    const token = localStorage.getItem("token");
    const usuarioLS = localStorage.getItem("usuario");
    const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

    const [loading, setLoading] = useState(true);
    const [edadStats, setEdadStats] = useState(initAges());
    const [jubGlobal, setJubGlobal] = useState(initJub());
    const [espStats, setEspStats] = useState({});
    const [espDict, setEspDict] = useState({});

    // catálogo especialidades
    useEffect(() => {
        (async () => {
            try {
                const res = await axios.get(`${API}/api/especialidades`, { headers });
                const arr = Array.isArray(res.data?.content) ? res.data.content : Array.isArray(res.data) ? res.data : [];
                const dict = arr.reduce((acc, it) => (acc[it.id] = it.nombre, acc), {});
                setEspDict(dict);
            } catch { /* noop */ }
        })();
    }, [API, headers]);

    // fetch todos los colegiados (paginando) y computar
    useEffect(() => {
        if (!open) return;
        setLoading(true);
        setEdadStats(initAges());
        setEspStats({});
        setJubGlobal(initJub());

        (async () => {
            try {
                const pageSize = 500;
                let ages = initAges();
                let esp = {};
                let jg = initJub(); // ahora guardamos total/H/M por grupo

                for (let p = 0; ; p++) {
                    const res = await axios.get(`${API}/api/admin/colegiados`, {
                        headers,
                        params: { page: p, size: pageSize, sortBy: "codigo", order: "asc", sinAdmins: true }
                    });
                    const data = res.data;
                    const content = Array.isArray(data?.content) ? data.content : Array.isArray(data) ? data : [];
                    if (content.length === 0) break;

                    for (const r of content) {
                        const s = sexoFmt(r?.sexo);

                        // Edades
                        const edad = calcEdad(leerFecha(r));
                        const buck = bucketEdad(edad);
                        ages[buck].total += 1;
                        if (s === "M") ages[buck].M += 1;
                        else if (s === "H") ages[buck].H += 1;

                        // Situación (jubilación) por sexo
                        const jubKey = getJubKey(r?.estado);
                        const slotJ = jg[jubKey];
                        slotJ.total += 1;
                        if (s === "M") slotJ.M += 1;
                        else if (s === "H") slotJ.H += 1;

                        // Especialidades
                        const ids = getEspecialidadesIds(r);
                        if (ids.length) {
                            for (const id of ids) {
                                const nombre = espDict[id] ?? String(id);
                                if (!esp[nombre]) {
                                    esp[nombre] = {
                                        JUB: { total: 0, H: 0, M: 0 },
                                        NO_JUB: { total: 0, H: 0, M: 0 },
                                        NC: { total: 0, H: 0, M: 0 }
                                    };
                                }
                                const slot = esp[nombre][jubKey];
                                slot.total += 1;
                                if (s === "M") slot.M += 1;
                                else if (s === "H") slot.H += 1;
                            }
                        }
                    }
                    if (content.length < pageSize) break;
                }

                setEdadStats(ages);
                setEspStats(esp);
                setJubGlobal(jg);
            } catch (e) {
                console.error("INE stats error", e?.response?.data || e);
            } finally {
                setLoading(false);
            }
        })();
    }, [open, API, headers, espDict]);

    const rowsEdad = [
        ["Menos 35", "MENOS_35"],
        ["35 a 44", "35_44"],
        ["45 a 54", "45_54"],
        ["55 a 64", "55_64"],
        ["65 a 69", "65_69"],
        ["70 y más", "70_MAS"],
        ["No consta", "NO_CONSTA"],
    ];

    const fechaStr = fmtFechaHora();
    const usuarioStr = usuario ?? usuarioLS ?? "";

    return (
        <Dialog fullScreen open={open} onClose={onClose}>
            <AppBar sx={{ position: "relative" }}>
                <Toolbar>
                    <IconButton edge="start" color="inherit" onClick={onClose} aria-label="close">
                        <CloseIcon />
                    </IconButton>
                    <Typography sx={{ ml: 2, flex: 1 }} variant="h6">Estadística INE</Typography>
                    <Button color="inherit" startIcon={<PrintIcon />} onClick={() => window.print()}>
                        Imprimir / PDF
                    </Button>
                </Toolbar>
            </AppBar>

            {/* Contenido imprimible */}
            <Box id="ine-print-root" sx={{ p: 2 }}>
                {/* Cabecera tipo informe */}
                <Box className="print-header" sx={{ mb: 2 }}>
                    <Typography variant="h5" sx={{ fontWeight: 700, letterSpacing: 0.2, mb: 0.5 }}>
                        Estadística INE
                    </Typography>
                    <Box sx={{ display: "flex", gap: 4, fontSize: 13, opacity: 0.9 }}>
                        <div><b>Fecha de informe:</b> {fechaStr}</div>
                        {usuarioStr ? <div><b>Usuario:</b> {usuarioStr}</div> : null}
                    </Box>
                </Box>

                {loading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <Grid container spacing={2}>
                        {/* Edad */}
                        <Grid item xs={12} md={8}>
                            <Paper className="print-avoid-break ine-card" sx={{ p: 2 }}>
                                <Typography className="ine-card-title" sx={{ mb: 1 }}>Estadística por edad</Typography>
                                <TableContainer>
                                    <Table size="small" className="ine-table">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell />
                                                <TableCell align="right"><b>TOTAL</b></TableCell>
                                                <TableCell align="right"><b>HOMBRES</b></TableCell>
                                                <TableCell align="right"><b>MUJERES</b></TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {rowsEdad.map(([label, key]) => (
                                                <TableRow key={key}>
                                                    <TableCell>{label}</TableCell>
                                                    <TableCell align="right">{edadStats[key].total}</TableCell>
                                                    <TableCell align="right">{edadStats[key].H}</TableCell>
                                                    <TableCell align="right">{edadStats[key].M}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Paper>
                        </Grid>

                        {/* Situación */}
                        <Grid item xs={12} md={4}>
                            <Paper className="print-avoid-break ine-card" sx={{ p: 2, height: "100%" }}>
                                <Typography className="ine-card-title" sx={{ mb: 1 }}>Situación</Typography>
                                <TableContainer>
                                    <Table size="small" className="ine-table">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell />
                                                <TableCell align="right"><b>TOTAL</b></TableCell>
                                                <TableCell align="right"><b>HOMBRES</b></TableCell>
                                                <TableCell align="right"><b>MUJERES</b></TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            <TableRow>
                                                <TableCell>Jubilados</TableCell>
                                                <TableCell align="right">{jubGlobal.JUB.total}</TableCell>
                                                <TableCell align="right">{jubGlobal.JUB.H}</TableCell>
                                                <TableCell align="right">{jubGlobal.JUB.M}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell>No Jubilados</TableCell>
                                                <TableCell align="right">{jubGlobal.NO_JUB.total}</TableCell>
                                                <TableCell align="right">{jubGlobal.NO_JUB.H}</TableCell>
                                                <TableCell align="right">{jubGlobal.NO_JUB.M}</TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell>No consta</TableCell>
                                                <TableCell align="right">{jubGlobal.NC.total}</TableCell>
                                                <TableCell align="right">{jubGlobal.NC.H}</TableCell>
                                                <TableCell align="right">{jubGlobal.NC.M}</TableCell>
                                            </TableRow>
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                                <Box sx={{ mt: 1, fontSize: 12, opacity: .7 }}>
                                    * “Jubilado” se detecta si el nombre del estado contiene la palabra <b>jubil</b>.
                                </Box>
                            </Paper>
                        </Grid>

                        {/* Especialidades */}
                        <Grid item xs={12}>
                            <Typography className="ine-section" sx={{ mt: 2, mb: 1 }}>Especialidad</Typography>
                        </Grid>

                        {Object.keys(espStats).sort((a, b) => a.localeCompare(b)).map((nombre) => {
                            const s = espStats[nombre];
                            const rows = [
                                ["Jubilados", s.JUB],
                                ["No Jubilados", s.NO_JUB],
                                ["No Consta", s.NC],
                            ];
                            return (
                                <Grid key={nombre} item xs={12} md={6} lg={4}>
                                    <Paper className="print-avoid-break ine-card" sx={{ p: 2, height: "100%" }}>
                                        <Typography className="ine-card-title" sx={{ mb: 1 }}>{nombre}</Typography>
                                        <TableContainer>
                                            <Table size="small" className="ine-table">
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell />
                                                        <TableCell align="right"><b>TOTAL</b></TableCell>
                                                        <TableCell align="right"><b>HOMBRES</b></TableCell>
                                                        <TableCell align="right"><b>MUJERES</b></TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {rows.map(([lbl, obj]) => (
                                                        <TableRow key={lbl}>
                                                            <TableCell>{lbl}</TableCell>
                                                            <TableCell align="right">{obj.total}</TableCell>
                                                            <TableCell align="right">{obj.H}</TableCell>
                                                            <TableCell align="right">{obj.M}</TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </Paper>
                                </Grid>
                            );
                        })}
                    </Grid>
                )}
            </Box>

            {/* PRINT CSS */}
            <style>{`
        /* Apariencia "INE" también en pantalla */
        .ine-card-title {
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: .3px;
          font-size: 14px;
        }
        .ine-section {
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: .3px;
          font-size: 16px;
        }
        .ine-table .MuiTableCell-root {
          font-size: 12px;
          padding-top: 6px;
          padding-bottom: 6px;
          border-bottom: 1px solid rgba(0,0,0,.12);
        }
        .ine-table .MuiTableHead-root .MuiTableCell-root {
          background: #f4f4f4;
          font-weight: 700;
          border-bottom: 1px solid rgba(0,0,0,.3);
        }

        @media print {
          @page { margin: 12mm; }
          body * { display: none !important; }

          /* muestra solo diálogo e informe */
          .MuiDialog-root,
          .MuiDialog-container,
          .MuiDialog-paperFullScreen,
          #ine-print-root,
          #ine-print-root * { display: revert !important; }

          .MuiBackdrop-root { display: none !important; }
          .MuiDialog-container { position: static !important; inset: auto !important; padding: 0 !important; }
          .MuiDialog-paperFullScreen {
            box-shadow: none !important;
            margin: 0 !important;
            width: auto !important;
            height: auto !important;
            overflow: visible !important;
            background: #fff !important;
          }

          .print-avoid-break { break-inside: avoid-page; page-break-inside: avoid; }

          .print-header {
            display: block !important;
            margin-bottom: 8px !important;
            border-bottom: 1px solid #bbb;
            padding-bottom: 6px;
          }

          /* Pie con numeración */
          body { counter-reset: page; }
          #ine-print-root::after {
            content: "Página " counter(page) " de " counter(pages);
            position: fixed;
            bottom: 6mm;
            right: 12mm;
            font-size: 11px;
            color: #333;
          }

          .ine-table .MuiTableCell-root { color: #000 !important; }
        }
      `}</style>
        </Dialog>
    );
}
